import { useSyncExternalStore, type Dispatch, type SetStateAction } from 'react'

export type GlobalStateSetter<T> = Dispatch<SetStateAction<T>>

type Listener = () => void

type Store<T> = {
  state: T
  listeners: Set<Listener>
}

interface GlobalStates extends Map<string, Store<unknown>> {}

// All named states are stored in this global map. Using globalThis instead of a
// module variable ensures state persists across Hot Module Reload (HMR) updates.
const global = globalThis as typeof globalThis & Partial<{globalStates: GlobalStates}>
if (!global.globalStates) {
  global.globalStates = new Map<string, Store<unknown>>()
}
const globalStates = global.globalStates


/**
* Factory function to create a global state hook
*
* Sample usage for definiing state:
* ```
*   export const useCurrentCustomer = createGlobalState<Customer | null>('currentCustomer', null)
*   export const useTheme = createGlobalState<'light' | 'dark'>('theme', 'light')
* ```
*
* Sample usage for using state:
* ```
*   const [currentCustomer, setCurrentCustomer] = useCurrentCustomer()
*   const [theme, setTheme] = useTheme()
* ```
*
*/
export function createGlobalState<T>(key: string, initialValue: T) {

  // Initialize if doesn't exist
  if (!globalStates.has(key)) {
    globalStates.set(key, {
      state: initialValue,
      listeners: new Set(),
    } as Store<T>)
  }

  const store = globalStates.get(key) as Store<T>

  // Get the current state
  const getSnapshot = (): T => {
    return store.state as T
  }

  // Subscribe to changes to the state
  const subscribe = (listener: Listener): (() => void) => {
    store.listeners.add(listener)
    return () => {
      store.listeners.delete(listener)
    }
  }

  // Notify all listeners of the state change
  const notify = (): void => {
    store.listeners.forEach(listener => listener())
  }

  // Set the current state to something new (or the same)
  const setState: GlobalStateSetter<T> = (newState: T | ((prev: T) => T)): void => {
    const nextState = typeof newState === 'function'
      ? (newState as (prev: T) => T)(store.state as T)
      : newState
    
    // Only update and notify if the state actually changed
    if (!Object.is(store.state, nextState)) {
      store.state = nextState
      notify()
    }
  }

  // Return the hook function
  return function useGlobalState(): [T, (newState: T | ((prev: T) => T)) => void] {
    // useSyncExternalStore is a React function that enables component re-rendering when the state changes
    const state = useSyncExternalStore(
      subscribe,
      getSnapshot,
      getSnapshot
    )
    return [state, setState]
  }

}


/** Clear all global states to maximize privacy and security */
export function clearGlobalState() {
  globalStates.clear()
}
